import React, { useEffect, useState } from 'react';
import { Button, Input, Modal, Space, Typography } from 'antd';
import { DeleteOutlined, ReloadOutlined } from '@ant-design/icons';
import { FctProgresoFases } from '../../../fct/react';
import type { FctEstadoFase } from '../../../fct/react/componentes/progreso';
import { CuentaWebFusion, EstadoFusion } from '../../../api/clientesDuplicadosApi';
import { InformeFusionView } from './InformeFusionView';
import { NotaProceso } from './NotaProceso';
import { cambiosDeReglas, lineasDeFusion } from './reglas';
import { AsistenteDuplicados } from './useAsistenteDuplicados';

const { Text } = Typography;

const MOTIVO_MINIMO = 10;
const MOTIVO_MAXIMO = 500;

/** Estado del informe en vivo: en curso (sondeo), terminado, con error o sin poder arrancar. */
type FctOverlayState = 'polling' | 'completed' | 'error' | 'launch_failed';

/** Una fase del tratamiento: se da por hecha cuando `faseActual` alcanza `completedWhenFase`. */
interface FctFaseUI {
  key: string;
  label: string;
  completedWhenFase: number;
}

/** Las cuatro fases del tratamiento, en el orden de §4.8 (aplicar → cuenta web → eliminar → informe). */
const FASES: FctFaseUI[] = [
  { key: 'aplicar', label: 'Fusionar datos y traspasar registros', completedWhenFase: 1 },
  { key: 'cuentaWeb', label: 'Resolver la cuenta web', completedWhenFase: 2 },
  { key: 'eliminar', label: 'Eliminar el registro duplicado', completedWhenFase: 3 },
  { key: 'informe', label: 'Cerrar el informe', completedWhenFase: 4 },
];

/**
 * Traduce (fase actual, estado del sondeo) al estado de cada fase de `FctProgresoFases`: hecha si
 * ya se alcanzó; en curso la que viene justo después mientras se sondea; fallida esa misma si el
 * proceso se detuvo (o la primera si ni arrancó); pendiente el resto. Al completar, todas hechas.
 */
const estadoFase = (fase: FctFaseUI, faseActual: number, state: FctOverlayState): FctEstadoFase => {
  if (state === 'completed' || faseActual >= fase.completedWhenFase) return 'hecha';
  const activa = faseActual === fase.completedWhenFase - 1;
  if (!activa) return 'pendiente';
  if (state === 'error' || state === 'launch_failed') return 'fallida';
  return 'en-curso';
};

/** Qué va a pasar con la cuenta web (§4.7), tal como lo enseña la simulación sin tocar GFW. */
const textoCuentaWeb = (c: CuentaWebFusion | null): { titulo: string; texto: string } => {
  if (!c) return { titulo: 'Cuenta web', texto: 'No se ha podido leer el estado de la cuenta web; se resolverá al aplicar.' };
  switch (c.caso) {
    case 'TRASPASAR':
      return {
        titulo: 'Cuenta web: cambia de dueño',
        texto: `La cuenta ${c.idClienteWebConservar} pasará a ${c.codigoConservado ?? 'el superviviente'}: código y email al día en la web, sin mover visitas.`,
      };
    case 'UNIR':
      return {
        titulo: 'Cuenta web: se unen',
        texto: `Cada ficha tiene la suya: las visitas de la cuenta ${c.idClienteWebEliminar} pasarán a la ${c.idClienteWebConservar} y la ${c.idClienteWebEliminar} se archivará.`,
      };
    default:
      // Solo el superviviente tiene cuenta: no se mueve, pero se pone al día en la web.
      if (c.idClienteWebConservar !== null) {
        return {
          titulo: 'Cuenta web: se pone al día',
          texto: `La cuenta ${c.idClienteWebConservar} del superviviente se queda como está; en la web se actualizarán su código (${c.codigoConservado ?? 'el del superviviente'}) y su email, que es el login, con el email principal que quede, si difieren.`,
        };
      }
      return { titulo: 'Cuenta web: nada que hacer', texto: 'Ninguna de las dos fichas tiene cuenta web.' };
  }
};

/**
 * Paso 5 (§4.5): simulación real antes de tocar nada (D-GC-37), motivo obligatorio (S-15),
 * confirmación con el código y el nombre del que se elimina en el botón, y ejecución con
 * informe en vivo (`FctProgresoFases`, progreso por fases del manual de marca digital).
 */
export const PasoTratamiento: React.FC<{ a: AsistenteDuplicados }> = ({ a }) => {
  const [modalAbierto, setModalAbierto] = useState(false);
  const e = a.ejecucion;

  useEffect(() => { if (e.estado === 'en_curso') setModalAbierto(true); }, [e.estado]);

  if (!a.comparacion || !a.fichaConserva || !a.fichaElimina) return null;
  const elimina = a.fichaElimina;

  const motivoValido = a.motivo.trim().length >= MOTIVO_MINIMO && a.motivo.trim().length <= MOTIVO_MAXIMO;
  const puedeAplicar = a.simulacionLimpia && motivoValido && e.estado !== 'en_curso' && e.estado !== 'completada';
  const reanudable = e.estado === 'error' && e.aplicado !== null;

  const confirmar = () => {
    Modal.confirm({
      title: `¿Eliminar ${elimina.codigo ?? elimina.idCliente} · ${elimina.nombreCompleto ?? ''}?`,
      content: (
        <span>
          Sus datos y registros pasarán a <Text strong>{a.fichaConserva!.codigo ?? a.fichaConserva!.idCliente}</Text>.
          El borrado es físico y no se puede deshacer; queda una foto de la ficha eliminada en el registro de la fusión.
        </span>
      ),
      okText: `Eliminar ${elimina.codigo ?? elimina.idCliente}`,
      okType: 'default', // destructiva: peligro sin primario (R-09)
      okButtonProps: { danger: true },
      cancelText: 'Cancelar',
      width: 520,
      onOk: () => { a.ejecutar(); },
    });
  };

  const estadoModal: FctOverlayState =
    e.estado === 'completada' ? 'completed'
      : e.estado === 'error' ? (e.fase === 0 ? 'launch_failed' : 'error')
        : 'polling';

  const cw = a.simulacion ? textoCuentaWeb(a.simulacion.cuentaWeb) : null;

  const pendientesReglas = e.estado === 'inactiva' ? cambiosDeReglas(a.comparacion, a.edicion) : 0;

  /*
   * Orden FIJO (Dev, 2026-09-24): lo que el operador rellena —motivo y botón— va arriba y no se
   * mueve; lo que llega después (simulación, avisos, errores) va debajo, en notas de una línea
   * con el detalle plegado. Antes, el resultado de la simulación aparecía encima del motivo y lo
   * empujaba hacia abajo mientras se escribía.
   */
  return (
    <Space direction="vertical" size={10} style={{ width: '100%' }}>
      {e.estado !== 'completada' && (
        <>
          <Input.TextArea
            rows={2}
            maxLength={MOTIVO_MAXIMO}
            showCount
            value={a.motivo}
            disabled={e.estado === 'en_curso' || e.aplicado !== null}
            onChange={(ev) => a.setMotivo(ev.target.value)}
            placeholder={`Motivo de la fusión (mínimo ${MOTIVO_MINIMO} caracteres). Queda en el registro.`}
            aria-label="Motivo de la fusión"
          />
          {reanudable ? (
            <Button danger icon={<ReloadOutlined />} onClick={() => a.ejecutar()}>
              Reanudar el tratamiento
            </Button>
          ) : (
            <Button danger icon={<DeleteOutlined />} disabled={!puedeAplicar} onClick={confirmar}>
              Eliminar {elimina.codigo ?? elimina.idCliente}
            </Button>
          )}
        </>
      )}

      {/* Lo que llega después: una línea cada cosa, detalle a demanda. */}
      {a.simulando && <NotaProceso tipo="info" titulo="Simulando la fusión (se deshace al terminar)…" />}

      {a.errorSimulacion && (
        <NotaProceso
          tipo="error"
          titulo={a.errorSimulacion.titulo ?? 'No se ha podido simular'}
          abierta
          detalle={
            <Space direction="vertical" size={4}>
              <span>{a.errorSimulacion.mensaje}</span>
              {a.errorSimulacion.faltas?.map((f) => <span key={f}>· {f}</span>)}
              <Space size={6} wrap>
                {a.errorSimulacion.seResuelveIntercambiando && a.puedeIntercambiar && (
                  <Button size="small" onClick={a.intercambiar}>Intercambiar columnas</Button>
                )}
                <Button size="small" icon={<ReloadOutlined />} onClick={a.simular}>Volver a simular</Button>
              </Space>
            </Space>
          }
        />
      )}

      {a.simulacion && e.estado === 'inactiva' && (
        <NotaProceso
          tipo={a.simulacionLimpia ? 'ok' : 'error'}
          titulo={a.simulacionLimpia
            ? `Simulación nº ${a.simulacion.idClienteFusion}: sin referencias pendientes`
            : `Simulación nº ${a.simulacion.idClienteFusion}: ${a.simulacion.idEstado === EstadoFusion.Simulada ? `${a.simulacion.pendientes.length} referencias pendientes` : a.simulacion.estado}; no se puede continuar`}
          abierta={!a.simulacionLimpia}
          detalle={
            <Space direction="vertical" size={6} style={{ width: '100%' }}>
              <InformeFusionView lineas={lineasDeFusion(a.simulacion)} />
              {!a.simulacionLimpia && (
                <span>Quedan referencias al duplicado o la fusión ha fallado. Corrige los datos en Gescom y vuelve a simular.</span>
              )}
              <Button size="small" type="link" icon={<ReloadOutlined />} onClick={a.simular} style={{ padding: 0 }}>Repetir la simulación</Button>
            </Space>
          }
        />
      )}

      {cw && e.estado === 'inactiva' && <NotaProceso tipo="info" titulo={cw.titulo} detalle={cw.texto} />}

      {pendientesReglas > 0 && (
        <NotaProceso
          tipo="aviso"
          titulo={`${pendientesReglas} dato${pendientesReglas === 1 ? '' : 's'} del duplicado que las reglas conservarían se perderán`}
          detalle="No los has pasado a la izquierda. Si los quieres, vuelve al paso 4 y pásalos (uno a uno o con «Aplicar reglas de dato ganador»)."
        />
      )}

      {e.estado === 'en_curso' && <NotaProceso tipo="info" titulo="Tratamiento en curso: no cierre la pantalla." />}

      {e.estado === 'error' && (
        <NotaProceso
          tipo="error"
          abierta
          titulo={e.error?.titulo ?? 'El tratamiento se ha detenido'}
          detalle={
            <Space direction="vertical" size={4} style={{ width: '100%' }}>
              <span>{e.error?.mensaje}</span>
              {e.error?.faltas?.map((f) => <span key={f}>· {f}</span>)}
              {e.idFusion !== null && <span>Fusión nº {e.idFusion}: lo ya hecho se conserva y se puede reanudar.</span>}
              {e.error?.seResuelveIntercambiando && a.puedeIntercambiar && (
                <Button size="small" onClick={a.intercambiar}>Intercambiar columnas</Button>
              )}
              {e.lineas.length > 0 && <InformeFusionView lineas={e.lineas} />}
            </Space>
          }
        />
      )}

      <FctProgresoFases
        abierto={modalAbierto && e.estado !== 'inactiva'}
        titulo={`Tratamiento del duplicado ${elimina.codigo ?? elimina.idCliente}`}
        fases={FASES.map((f, i) => ({
          nombre: f.label,
          estado: estadoFase(f, e.fase, estadoModal),
          // El resumen de éxito va como detalle de la última fase (antes, banda bajo la lista).
          detalle: estadoModal === 'completed' && i === FASES.length - 1
            ? `Fusión nº ${e.idFusion} completada. El detalle queda en el panel de proceso.`
            : undefined,
        }))}
        error={e.estado === 'error' ? `${e.error?.titulo ? `${e.error.titulo}: ` : ''}${e.error?.mensaje ?? ''}` : undefined}
        onCerrar={() => {
          setModalAbierto(false);
          if (e.estado === 'completada') a.irAPaso(6);
        }}
      />
    </Space>
  );
};
